import { useContext, useMemo } from 'react';
import { Body1Strong, Divider, Link as FluentLink, makeStyles } from '@fluentui/react-components';

import { getLiteratureShortRef } from '@modules/literatures';
import DocumentContext from '../../contexts/DocumentContext.context';
import { TReference } from '../../types';
import { encodeLiteratureDrag } from '../../tiptap/nodes/literatureCitation/plugins/getLiteratureCitationDropPlugin';

import { collectNodesOfType } from '@modules/documents/helpers/collectNodes.helper';
import { wholeDoc } from '@modules/documents/largeDocument';
import useScrollContent from '@modules/documents/hooks/useScrollContent.hook';
import { EItemScreenMode, getVersionId, isTDraft, useItemScreenStore } from '@modules/items';
import getReferenceItemLabel from '@modules/documents/helpers/getReferenceItemLabel.helper';
import { isRegistrationSettled } from '@modules/documents/helpers/isRegistrationSettled.helper';
import { TDisplayItem } from '@shared/components';
import { getCaptionPrefix } from '@shared/backendFrontend/captionHelpersFe';
import { buildHref } from '@shared/utils';
import { BranchCompare20Regular } from '@fluentui/react-icons';
import { getBlockDisplayName } from '../../helpers/blocks.helper';
import { TERMINOLOGY } from '@shared/i18n';
import { SignInContext } from '../../../users';
import {
  ControlledTooltip,
  SidebarTree,
  buildTreeItems,
  TSidebarTreeItem,
  TSidebarTreeSection,
  ReplicaListingsSection,
} from '@shared/components';
import TemplateUsageTree from './TemplateUsageTree';

const WARN = '⚠️';

const useStyles = makeStyles({
  potentialImpactsButton: {
    width: 'fit-content',
    padding: '0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'start',
    gap: '.1rem',
  },
});

const NavDocumentInsights = () => {
  const { doc, version, editor, resolvedItemNames, versionId, captionsMap, resolveCaptionLabel, replica, isEdit } =
    useContext(DocumentContext);
  const {
    organization: { mock },
  } = useContext(SignInContext);

  // in-refs:
  const inRefs: TDisplayItem[] = useMemo(() => {
    return doc.inRefs
      .filter(
        (ref) =>
          ref.sourceDocId !== doc._id &&
          (ref.versionId
            ? (version ? getVersionId(version) : 'draft') === ref.versionId
            : (doc.draft || doc.versions[0]) === version)
      )
      .map(
        (ref): TDisplayItem => ({
          id: ref.id,
          duplicateId: ref.sourceDocId,
          text: getReferenceItemLabel(ref.sourceDocName),
          href: buildHref(`/document/${ref.sourceDocId}`, { view: 'latest', elem: ref.id }),
        })
      );
  }, [version, doc.inRefs, doc._id, doc.draft, doc.versions]);

  // out-refs:
  const scrollContent = useScrollContent();
  const outRefs: TDisplayItem[] = useMemo(() => {
    if (!version) return [];

    // The whole document, not the editor: this panel lists the references in the DRAFT, and a large
    // document has only one part in the editor.
    const refNodes = collectNodesOfType(wholeDoc(editor) ?? editor.state.doc, 'reference').filter((ref) =>
      isRegistrationSettled(ref.attrs)
    );
    const selfRefNodes = refNodes.filter((ref) => ref.attrs.versionMode === 'SELF');

    const refs: TDisplayItem[] = selfRefNodes.map((ref) => {
      let text = `Deleted section ${WARN}`;
      const attrs = ref.attrs as TReference & { originalText: string };
      const bookmarkId = attrs.bookmark?.id;
      if (bookmarkId) {
        if (!captionsMap) text = 'Loading...';
        else {
          const entry = captionsMap[bookmarkId];
          text = entry
            ? getCaptionPrefix(entry, resolveCaptionLabel)
            : `${attrs.originalText || `Unknown ${TERMINOLOGY.reference.lower}`} ${WARN}`;
        }
      } else {
        const heading = version.headings.find((h) => h.id === attrs.headingId);
        const isH5Ref = heading?.level === 5 && mock?.isH5RefEnabled;
        if (heading) text = !isH5Ref ? `${heading.numbering} ${heading.text}` : heading.text;
      }
      return {
        id: ref.attrs.id,
        text,
        onClick: () => scrollContent({ value: ref.attrs.id }),
      };
    });
    for (const refId in doc.refsData) {
      if (!version.outRefs.includes(refId)) continue;

      const refData = doc.refsData[refId];

      let text = getReferenceItemLabel(refData.itemName);
      if (refData.versionId) text += ` (${refData.versionId === 'draft' ? '' : 'v'}${refData.versionId})`;
      if (refData.headingId) {
        text += ' › ';
        const isH5Ref = refData.headingData?.numbering.split('.').length === 6 && mock?.isH5RefEnabled;
        if (refData.headingData)
          text += !isH5Ref ? `${refData.headingData.numbering} ${refData.headingData.text}` : refData.headingData.text;
        else text += 'Deleted section';
      }

      refs.push({
        id: refId,
        text,
        onClick: () => scrollContent({ value: refId }),
      });
    }
    return refs;
  }, [doc.refsData, version?.outRefs, version?.headings, captionsMap, resolveCaptionLabel]);

  // reusable blocks:
  const reusableBlocks: TDisplayItem[] = useMemo(() => {
    if (!version) return [];

    return version.blocks
      .filter((block) => !block.dateDeleted)
      .map((block) => {
        const blockData = doc.blocksData[block.id];

        // not expected
        if (!blockData) {
          console.error(`${TERMINOLOGY.block.upper} data not found for block ID: ${block.id}`);
          return { id: block.id, text: 'Unknown', onClick: () => scrollContent({ value: block.id }) };
        }

        const blockVersionEntry = versionId ? blockData.versions[versionId] : undefined;
        const text = getBlockDisplayName(
          doc,
          version,
          resolvedItemNames,
          blockData,
          block.versionMode,
          blockVersionEntry
        );

        return {
          id: block.id,
          text,
          onClick: () => scrollContent({ value: block.id }),
        };
      });
  }, [version, doc.blocksData, resolvedItemNames]);

  const bibliographyItems: TSidebarTreeItem[] = useMemo(() => {
    if (!version) return [];
    // Drafts resolve to the literature's latest completed version, so there's no versionId to pin - only version docs carry frozen tuples.
    const entries: { itemId: string; versionId?: string }[] = !isTDraft(version)
      ? version.literatureCitations.map((c) => ({ itemId: c.itemId, versionId: c.versionId }))
      : (doc.draft?.literatureCitations ?? []).map((id) => ({ itemId: id }));
    return entries.map(({ itemId, versionId }, idx) => {
      const entry = doc.resolvedLiteratureCitations?.[itemId];
      const displayVersionId = versionId ?? entry?.latestVersionId;
      const content = displayVersionId ? entry?.versions[displayVersionId] : undefined;
      const href = versionId ? `/literature/${itemId}?view=${versionId}` : `/literature/${itemId}`;
      const shortRef = content ? getLiteratureShortRef(content) : itemId;
      const index = idx + 1;
      return {
        id: itemId,
        label: shortRef,
        renderLabel: () => (
          <ControlledTooltip
            enable={isEdit}
            content="Drag to insert into document."
            relationship="description"
            positioning="after"
            withArrow
            showDelay={500}
          >
            <div
              className={`flex gap-2 min-w-0 ${isEdit ? 'cursor-grab select-none' : ''}`}
              draggable={isEdit}
              onDragStart={
                isEdit
                  ? (e) => {
                      e.dataTransfer.setData('text/plain', encodeLiteratureDrag(itemId));
                      e.dataTransfer.effectAllowed = 'copy';
                    }
                  : undefined
              }
            >
              <span className="text-neutral-500 shrink-0">[{index}]</span>
              <FluentLink
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 min-w-0 truncate"
                draggable={false}
              >
                {shortRef}
              </FluentLink>
            </div>
          </ControlledTooltip>
        ),
      };
    });
  }, [doc, version, isEdit]);

  // Impact analysis features
  const { screenMode, setScreenMode } = useItemScreenStore();

  const togglePostChangeImpactAnalysisResults = () => {
    if (screenMode === EItemScreenMode.IMPACT_ANALYSIS_SUMMARY) {
      setScreenMode();
    } else {
      setScreenMode(EItemScreenMode.IMPACT_ANALYSIS_SUMMARY);
    }
  };

  const classes = useStyles();

  const insightSections = useMemo<TSidebarTreeSection[]>(() => {
    if (!version) return [];

    const versionLabel = !isTDraft(version) ? `v${version.id}` : 'this draft';

    return [
      {
        id: 'bibliography',
        label: <Body1Strong>Bibliography</Body1Strong>,
        items: bibliographyItems,
        hideWhenEmpty: true,
      },
      {
        id: 'references',
        label: (
          <Body1Strong>
            {TERMINOLOGY.reference.upperPlural} in {versionLabel}
          </Body1Strong>
        ),
        items: buildTreeItems(outRefs, 'references'),
      },
      {
        id: 'blocks',
        label: (
          <Body1Strong>
            {TERMINOLOGY.block.upper} in {versionLabel}
          </Body1Strong>
        ),
        items: buildTreeItems(reusableBlocks, 'blocks'),
      },
      // No other document can reference a Change Template document.
      ...(doc.parentType === 'CHANGE_TEMPLATE'
        ? []
        : [
            {
              id: 'inRefs',
              label: <Body1Strong>Other documents referencing {versionLabel}</Body1Strong>,
              items: buildTreeItems(inRefs, 'inRefs'),
            },
          ]),
    ];
  }, [doc, version, outRefs, reusableBlocks, inRefs, bibliographyItems]);

  const insightsTreeProps = useMemo(
    () => (insightSections.length > 0 ? { defaultOpenItems: insightSections.map((section) => section.id) } : undefined),
    [insightSections]
  );

  if (!version) return <p className="pl-5">No draft or version found.</p>;

  return (
    <div className="mx-2">
      {/* A Change Template document is stored as a template, but no document can use it. */}
      {doc.isTemplate && doc.parentType !== 'CHANGE_TEMPLATE' && (
        <>
          <p className="px-3 pb-2">
            <Body1Strong>Documents using this {TERMINOLOGY.template.lower}</Body1Strong>
          </p>
          <div className="px-2">
            <TemplateUsageTree templateUsage={doc.templateUsage} versions={doc.versions} />
          </div>

          <Divider className="py-3" />
        </>
      )}

      <div className="px-2">
        <SidebarTree ariaLabel="Document insights" sections={insightSections} treeProps={insightsTreeProps} />
      </div>

      <ReplicaListingsSection replica={replica} replicaListings={doc.replicaListings} versions={doc.versions} />

      {version && !isTDraft(version) && version.impactAnalysis && (
        <div className="flex justify-start items-center gap-2 px-3 pt-4 pb-2">
          <Body1Strong>Impacts of {`v${version.id}`} on other files:</Body1Strong>
          <FluentLink className={classes.potentialImpactsButton} onClick={togglePostChangeImpactAnalysisResults}>
            <BranchCompare20Regular />
            <span>{version.impactAnalysis.impacts.length}</span>
          </FluentLink>
        </div>
      )}
    </div>
  );
};

export default NavDocumentInsights;
